/**
 * Fiche séance n°9 — Formation PASSERELLE A2 → A (7h obligatoire)
 *
 * Source : notes manuscrites de moniteur ECSR (mai 2026).
 * Pages scans originaux dans /img/cours-source/passerelle/ (WA0000 → WA0004).
 *
 * Cadre légal : arrêté du 1er juin 2016 (formation 7h obligatoire pour
 * passage A2 → A après 2 ans de permis A2).
 * Structure : 2h théorie (salle) + 5h pratique (plateau + circulation).
 */

export const passerelleA2ACours = {
  slug: 'passerelle-a2-a',
  number: 9,
  title: 'Formation Passerelle A2 → A (7h)',
  subtitle:
    "Programme complet de la formation 7h obligatoire pour passer du permis A2 au permis A après 2 ans : 2h théorie + 5h pratique (plateau + circulation).",
  duration: '7h (2h salle + 5h pratique)',
  level: 'Titulaires permis A2 depuis 2 ans minimum',
  format: 'Théorie salle (2h) + Plateau (2h) + Circulation (3h)',
  accent: '#cf2031',
  icon: 'Bike',

  phraseCle:
    "Le permis A, c'est 70 kW au lieu de 35 kW. Le double de puissance ne pardonne aucune approximation : adhérence, visibilité, anticipation deviennent vitaux.",

  source: {
    label: 'Notes moniteur ECSR — Formation Passerelle (arrêté 1er juin 2016)',
    pages: 'Cahier moniteur — 5 pages',
  },

  remc: {
    competences: [
      {
        code: 'C2',
        label: 'Appréhender la route et circuler dans des conditions normales',
        souscompetences: [
          'C2.2 — Positionner le véhicule sur la chaussée',
          "C2.3 — Adapter l'allure aux situations",
          'C2.4 — Détecter les indices utiles',
        ],
      },
      {
        code: 'C3',
        label: 'Circuler dans des conditions difficiles et partager la route',
        souscompetences: [
          'C3.6 — Choisir et réaliser une trajectoire de sécurité',
          'C3.7 — Freiner et immobiliser le véhicule en toutes circonstances',
          'C3.8 — Éviter un obstacle imprévu',
        ],
      },
    ],
    matrice_gde: 'Niveau 3 — Contexte de la conduite (montée en puissance moteur)',
    ppo: 'Pédagogie Par Objectifs : verbes mesurables (citer, identifier, exécuter, anticiper).',
  },

  objectifs: {
    savoir:
      "À la fin de cette formation, TU SAURAS identifier les principaux facteurs d'accident en moto (vitesse, alcool, équipement, freinage, slalom, évitement) et tu pourras citer les 2 critères de sécurité à vérifier AVANT chaque départ : adhérence et visibilité.",
    savoirFaire:
      "À la fin de cette formation, TU SAURAS FAIRE en plateau les exercices imposés (démarrage-arrêt, parcours lent, slalom, freinage d'urgence, évitement) et en circulation tu seras capable d'adapter ta trajectoire et ton allure à toute situation rencontrée (intersection, virage, giratoire, STOP).",
    savoirEtre:
      "Au sortir de cette formation, TU ADOPTERAS une posture d'analyse permanente : avant de partir (équipement, pneus), pendant ta conduite (anticipation, regard porté loin), après ton trajet (débrief honnête). Tu intégreras que la puissance supplémentaire du A t'impose une humilité supplémentaire, pas l'inverse.",
  },

  prerequis: [
    'Permis A2 obtenu depuis au moins 2 ans (date sur le titre)',
    'Convocation officielle remise par l\'école de conduite',
    'EPI complets : casque ECE 22.06, gants CE EN 13594, blouson + pantalon CE, bottes',
    'Moto-école : 35 kW au minimum (idéalement la moto utilisée pour l\'examen)',
  ],

  supports: [
    'Cahier de formation Passerelle (livret élève)',
    'Vidéos statistiques ONISR / MAIDS 2024',
    'Fiche R413-17 (sanctions vitesse) + Fiche sanctions alcool',
    'Plateau homologué (parcours lent, slalom, freinage, évitement)',
    'Moto de l\'auto-école + EPI complet',
    'Itinéraire de circulation balisé (ville + route + rocade)',
  ],

  deroule: [
    {
      etape: 1,
      duree: '15 min',
      titre: 'Tour de table & cadre de la formation',
      role: 'Animateur',
      animation:
        "Bienvenue ! On va commencer par faire connaissance — chacun à votre tour, dites-moi votre prénom, depuis combien de temps vous avez le A2, quelle moto vous comptez acheter, et UNE peur ou UNE question que vous avez en passant au A. Je reformulerai pour être sûr qu'on s'est bien compris. Le programme : 7h (2h théorie + 5h pratique). Et l'objectif n'est PAS de vous faire valider un examen — c'est de vous donner les bons réflexes pour les 70 kW à venir.",
      questions_eleve: [
        "Qui peut me dire la principale différence entre A2 et A ?",
        "Qu'est-ce qui vous fait le plus peur en passant à 70 kW ?",
        "Avez-vous déjà eu une frayeur en A2 ? Racontez.",
      ],
      attendu:
        "Climat de confiance instauré, peurs verbalisées, contrat pédagogique posé : c'est une formation, pas un examen.",
    },
    {
      etape: 2,
      duree: '20 min',
      titre: 'Statistiques accidentologie 2RM',
      role: 'Apport théorique',
      animation:
        "Je vais vous montrer les chiffres ONISR 2024. Regardez : 25 % des morts sur la route sont des motards alors que la moto représente seulement 2 % du trafic. 40 % des accidents mortels surviennent en virage. Et plus on monte en cylindrée, plus la part d'accidents mortels augmente. Mon but n'est pas de vous faire peur, c'est de RECONTEXTUALISER : la moto que tu vas acheter te tuera deux fois plus vite qu'une A2.",
      questions_eleve: [
        "Pourquoi le passage de A2 à A est statistiquement le plus à risque dans la première année ?",
        "À quel moment dans une vie de motard surviennent le plus d'accidents ?",
      ],
      attendu:
        "Tu prends conscience que la transition A2→A est une période à RISQUE accru (suréquipement subjectif, sous-estimation des nouvelles capacités machine).",
    },
    {
      etape: 3,
      duree: '40 min',
      titre: 'Facteurs d\'accident en 2RM (1/2) — Vitesse · Alcool · Équipement',
      role: 'Apport théorique + échange',
      animation:
        "On va décomposer ensemble 3 facteurs prioritaires. (1) LA VITESSE — ton champ visuel se réduit avec la vitesse : 180° à l'arrêt, 45° à 100 km/h, 30° à 120 km/h. La distance d'arrêt, c'est ta distance de réaction + ta distance de freinage. Attention à la différence entre excès et vitesse excessive (R413-17). (2) ALCOOL & SUBSTANCES — 30 % des morts moto. Pour vous en permis probatoire : 0 g/L tolérance (R234-1). (3) ÉQUIPEMENT — pinlock sur la visière, équipement hiver, airbag CE EN 1621-4 niveau 2.",
      questions_eleve: [
        "À 100 km/h, ton champ visuel est de combien de degrés ?",
        "Quelle différence entre excès de vitesse et vitesse excessive ?",
        "L'airbag est-il obligatoire ?",
      ],
      attendu:
        "À la fin de cette étape, tu sauras citer les 3 facteurs aggravants et leur impact mesurable sur ta sécurité.",
    },
    {
      etape: 4,
      duree: '25 min',
      titre: 'Facteurs d\'accident en 2RM (2/2) — Freinage · Slalom · Évitement',
      role: 'Apport théorique',
      animation:
        "Maintenant on attaque 3 gestes techniques. (1) LE FREINAGE — frein arrière seul : ça stabilise. Frein avant seul : 70 % de l'efficacité mais risque de blocage. Les 2 freins combinés : c'est ça la règle. On va comprendre ensemble le transfert de masse. (2) LE SLALOM — vous utiliserez le contre-braquage, le regard porté loin, le gaz constant. (3) L'ÉVITEMENT — c'est un contre-braquage en 2 temps : esquive + remise en ligne. Ces 3 gestes, vous allez les PRATIQUER au plateau cet après-midi.",
      questions_eleve: [
        "Quel frein utilise-t-on en priorité ? Pourquoi ?",
        "Qu'est-ce que le contre-braquage ?",
        "Évitement : on tourne d'abord à droite ou à gauche ?",
      ],
      attendu:
        "À la fin de cette étape, tu SAURAS VERBALISER les 3 gestes techniques avant de les pratiquer (théorie → pratique).",
    },
    {
      etape: 5,
      duree: '30 min',
      titre: 'Cas d\'accidents commentés — CIF (Champ d\'Investigation Fondamental)',
      role: 'Étude de cas',
      animation:
        "Je vais vous présenter 4 cas-types vécus en formation. (A) STOP non respecté → écrasement à l'intersection. (B) Tournant pris trop large → tu te retrouves en contresens. (C) Giratoire à 2 voies → tu te prends une porte de voiture. (D) Intersection sans visibilité → angle mort d'un camion. Pour chaque cas, on applique la méthode CIF : Champ visuel observé / Investigation des indices / Fond d'analyse du risque. Vous allez analyser en binôme puis débriefer ensemble.",
      questions_eleve: [
        "Que pouvait FAIRE le motard pour éviter cet accident ?",
        "Quel(s) indice(s) avait-il oublié de regarder ?",
        "À quelle vitesse aurait-il dû aborder cette situation ?",
      ],
      attendu:
        "À la fin de cette étape, tu SAURAS analyser une situation accidentelle en isolant les indices manqués et les actions qui auraient pu l'éviter.",
    },
    {
      etape: 6,
      duree: '2h',
      titre: 'Plateau Passerelle — Exercices techniques',
      role: 'Pratique encadrée',
      animation:
        "On passe à la moto. Voici le cycle d'exercices au plateau. ① Démarrage-arrêt en bout de piste — tu démarres, tu accélères, tu freines à un repère, tu repars. ② Demi-tour (DT) en 13 pas, puis en 4 pas (huit/parcours en 8), puis avec arrêt-départ. ③ Slalom lent à 4 pas entre les cônes (ou 3 si tu te sens fluide). ④ Parcours lent : 5 pas en mode A2 d'abord, puis en mode A. ⑤ Freinage d'urgence à 50 km/h. ⑥ Évitement d'obstacle. On répète chaque exercice jusqu'à ce que ce soit fluide. Pas de chrono, pas de course — la qualité du geste, c'est tout.",
      questions_eleve: [
        "Où regardes-tu pendant le demi-tour ?",
        "Quelle main gère le frein, quelle main gère l'embrayage en parcours lent ?",
        "Comment sais-tu que tu vas trop vite à l'entrée du slalom ?",
      ],
      attendu:
        "Tu deviens FLUIDE sur 80 % des passages dans chaque exercice, sans pied à terre intempestif.",
    },
    {
      etape: 7,
      duree: '3h',
      titre: 'Circulation — Avant de partir & Audit de conduite',
      role: 'Pratique en suivi',
      animation:
        "Avant de partir, on va systématiser : check pneus (chauds ?), check équipement complet, check route (état, dangers). Puis on roule : 3 élèves max + moi en voiture suiveuse avec la radio. Chacun aura 30 minutes minimum. Ton objectif : appliquer en permanence les 2 critères de sécurité — ADHÉRENCE (état route, dangers, état pneus) et VISIBILITÉ (positionnement chaussée, sortie de virage, allure adaptée, trajectoires de sécurité). J'observerai ta perception, ton analyse, ta décision, ton action. On débriefera individuellement puis collectivement à la fin.",
      questions_eleve: [
        "Quel est ton ressenti après ces 30 minutes ?",
        "Quelle situation t'a le plus mis en difficulté ?",
        "Quels indices as-tu manqués au giratoire de la rue X ?",
      ],
      attendu:
        "À la fin de cette étape, tu SAURAS auto-évaluer ta conduite et citer 2-3 axes d'amélioration concrets sur lesquels travailler.",
    },
    {
      etape: 8,
      duree: '15 min',
      titre: 'Clôture & Tour de table final',
      role: 'Animateur',
      animation:
        "On clôture en tour de table : chacun cite UN apprentissage majeur retenu aujourd'hui et UNE action concrète qu'il appliquera dès demain en moto. Je vous remets le certificat de formation (CEPC obligatoire pour le passage du A) — il est valide pour l'enregistrement en préfecture. Avant de partir, je vous donne mes conseils personnalisés à chacun.",
      questions_eleve: [
        "Si tu devais résumer la formation en 1 phrase, ce serait quoi ?",
        "Sur quoi vas-tu travailler en priorité sur ta prochaine sortie ?",
      ],
      attendu:
        "Clôture positive, certificat remis, tu repars avec un plan d'action personnel clair pour les prochaines semaines.",
    },
  ],

  contenus: [
    {
      id: 'criteres-securite-depart',
      titre: 'Les 2 critères avant tout départ',
      page_source: 'Passerelle — Avant de partir en circulation',
      points_cles: [
        'ADHÉRENCE : état de la route (humide, gravillons, gel), dangers visibles, état des pneus (chauds ou froids ?)',
        'VISIBILITÉ : où me positionner sur la chaussée, anticipation des sorties de virage SURPRISE, adapter l\'allure à la situation, trajectoires de sécurité',
        'Consignes de sécurité formateur (radio, signaux convenus)',
        'Distances de sécurité avec les véhicules et entre élèves',
      ],
      schema: 'Schéma champ visuel : 180° à l\'arrêt → 45° à 100 km/h → 30° à 120 km/h',
    },
    {
      id: 'facteurs-accident',
      titre: '8 facteurs d\'accident en 2RM',
      page_source: 'Notes moniteur — pages 1 et 3',
      points_cles: [
        '① Vitesse (champ visuel rétréci, distance d\'arrêt = réaction + freinage)',
        '② Alcool et substances psychoactives (R234-1, R234-3)',
        '③ Équipement (pinlock, équipement hiver, airbag)',
        '④ Freinage (arrière, avant, combiné — privilégier les 2)',
        '⑤ Slalom (contre-braquage, regard, gaz constant)',
        '⑥ Évitement (contre-braquage 2 temps, remise en ligne)',
        '⑦ STOP / intersection (angle mort, voiture qui coupe)',
        '⑧ Virages (trajectoire de sécurité E-I-E adaptée à la route)',
      ],
    },
    {
      id: 'plateau-exercices',
      titre: 'Plateau Passerelle — Exercices imposés',
      page_source: 'Notes moniteur — page 2',
      points_cles: [
        '① Démarrage-arrêt — DT bout de piste',
        '② Exercice DT (Demi-Tour) : 13 pas puis 4 pas, huit/parcours en 8, avec arrêt-départ',
        '③ Slalom lent : 4 pas entre cônes (ou 3 si difficile)',
        '④ Parcours lent A : 5 pas',
        '⑤ Parcours lent A2 (en référence)',
        '⑥ Freinage d\'urgence (à pratiquer en fin de plateau)',
        '⑦ Évitement (à pratiquer en fin de plateau)',
      ],
      schema: 'Plans de plateau avec cônes — voir scans WA0002',
    },
    {
      id: 'forces-ordre-respect',
      titre: "Posture ECSR — Respect des forces de l'ordre",
      page_source: 'Principe pédagogique ECSR transversal',
      points_cles: [
        "Les forces de l'ordre (gendarmerie nationale, police nationale, police municipale) sont en TÊTE de la hiérarchie des priorités routières — avant feux, panneaux, marquage",
        "Avec le permis A (> 35 kW), les contrôles sont plus fréquents : la moto puissante attire l'attention (BAC moto, motards de la gendarmerie)",
        "Ce ne sont pas des adversaires : ce sont les garants du cadre légal qui PROTÈGE le motard (vitesse, équipement, état du véhicule)",
        "Réflexe à l'arrêt : couper le moteur, retirer le casque, mains visibles sur le guidon, présenter calmement les documents",
        "Documents à présenter : permis A + carte grise + assurance + certificat Passerelle (utile dans les 12 mois post-formation)",
        "Échange courtois → indulgence fréquente sur les détails ; échange agressif → aggravation systématique",
        "Outrage à agent (R434-2) : 7 500 € + 6 mois ; rébellion (R433-6) : 1 an + 15 000 €",
        "Message ECSR : « Le permis A te donne 70 kW, pas le droit de t'estimer au-dessus des règles. Le respect des forces de l'ordre EST une compétence du motard mature. »",
      ],
    },
  ],

  evaluation: {
    type: 'QCM oral + débrief final (la Passerelle n\'a PAS d\'examen, seulement un suivi)',
    duree: '15 min',
    questions: [
      {
        q: 'Quelle est la principale différence légale entre permis A2 et A ?',
        choix: [
          'Aucune, c\'est juste un papier',
          'Le permis A autorise toutes les motos sans limite de puissance',
          'Le permis A interdit le transport de passager pendant 1 an',
          'Le permis A oblige à rouler avec un disque A pendant 6 mois',
        ],
        bonne: 1,
        explication:
          "Le permis A autorise les motos sans limitation de puissance (> 35 kW). Le permis A2 est limité à 35 kW (47 CV). La passerelle = 7h de formation après 2 ans de A2.",
      },
      {
        q: 'À 100 km/h, le champ visuel d\'un motard est environ :',
        choix: ['180°', '90°', '45°', '20°'],
        bonne: 2,
        explication:
          "Le champ visuel passe de 180° à l'arrêt à environ 45° à 100 km/h, et descend à 30° à 120 km/h. C'est la « vision tunnel » : la conscience périphérique disparaît.",
      },
      {
        q: 'Quels freins utiliser EN PRIORITÉ lors d\'un freinage d\'urgence ?',
        choix: [
          'Frein arrière seul (plus stable)',
          'Frein avant seul (70 % de l\'efficacité)',
          'Les 2 freins combinés et dosés',
          'Le frein moteur en rétrogradant',
        ],
        bonne: 2,
        explication:
          "Les 2 freins combinés et dosés sont la règle. Le frein avant fournit ~70 % de l'efficacité mais utilisé seul = risque de blocage roue avant + chute. Le frein arrière seul = stabilisation mais inefficace. Combinés = équilibre adhérence + efficacité.",
      },
      {
        q: 'Les 2 critères de sécurité à vérifier AVANT chaque départ sont :',
        choix: [
          'Vitesse et alcool',
          'Adhérence et visibilité',
          'Équipement et bagages',
          'Carburant et lubrification',
        ],
        bonne: 1,
        explication:
          "Adhérence (état route + état pneus chauds/froids) ET Visibilité (positionnement chaussée, anticipation sortie de virage, adaptation allure) sont les 2 critères de base de toute trajectoire de sécurité.",
      },
      {
        q: 'En slalom lent, où dois-tu poser ton regard ?',
        choix: [
          'Sur la roue avant',
          'Sur le cône que tu vas dépasser',
          'Sur le cône suivant (1 cône d\'avance)',
          'Sur le sol entre les cônes',
        ],
        bonne: 2,
        explication:
          "La règle des pros : regard 1 cône d'avance. Tu vas où tu regardes. Si tu fixes la roue ou le cône immédiat, la moto se ferme. Avec 1 cône d'avance, la trajectoire s'ouvre naturellement.",
      },
    ],
    cas_pratique: {
      enonce:
        'Tu arrives en moto A (70 kW) sur une route mouillée, en sortie de virage à droite. Une voiture en face commence à mordre la ligne médiane. Que fais-tu ?',
      reponse_attendue:
        "Adhérence dégradée (pluie) → marge supplémentaire. Visibilité : je suis déjà sur la partie droite (R412-9). Action : je relâche le gaz, je me déporte ENCORE plus à droite tout en gardant ma marge accotement, je freine MOINS (cercle d'adhérence dégradé), je porte mon regard vers la sortie. Je ne fais PAS d'écart brusque (perte d'adhérence). Si vraiment la voiture coupe : klaxon + freinage progressif + appui guidon droite pour serrer.",
    },
  },

  conseils_jury: [
    "Cette formation n'a PAS d'examen final — c'est un SUIVI pédagogique. Le certificat est délivré en fin de 7h, peu importe le « niveau » de l'élève.",
    "Le formateur DOIT signer le livret d'apprentissage de l'élève + remettre l'attestation officielle (modèle CEPC) à transmettre à la préfecture.",
    "L'objectif n'est pas de « valider un examen » mais de transférer des compétences et faire prendre conscience du risque accru du passage A2 → A.",
    "Beaucoup d'élèves arrivent avec une attitude « j'ai déjà mon A2, je connais ». Le tour de table initial doit DÉSACTIVER cette posture (questions « avez-vous déjà eu une frayeur ? »).",
    "En circulation : 30 min par élève MINIMUM imposées par l'arrêté du 1er juin 2016. Ne pas raccourcir même si pression horaire.",
  ],

  references: [
    {
      titre: "Arrêté du 1er juin 2016 (modifié)",
      detail:
        "Relatif à la formation de 7 heures obligatoire pour les titulaires de la catégorie A2 du permis de conduire souhaitant accéder à la catégorie A. Définit le programme : 2h théorie + 2h plateau + 3h circulation, certificat de suivi obligatoire.",
    },
    {
      titre: "Article R221-3 du Code de la route",
      detail:
        "Conditions d'accès à la catégorie A : titulaire du permis A2 depuis 2 ans minimum + suivi de la formation de 7h dans une école de conduite agréée.",
    },
    {
      titre: "Article R413-17 du Code de la route",
      detail:
        "Sanctions vitesse : retrait de points et amendes selon dépassement. Rappel jeune permis : période probatoire 3 ans (2 ans en cas de conduite accompagnée).",
    },
    {
      titre: "Article R234-1 et R234-3 du Code de la route",
      detail:
        "Conduite sous l'empire d'un état alcoolique : seuil 0,5 g/L pour permis confirmé, 0,2 g/L pour jeune permis (probatoire). Sanctions identiques pour stupéfiants (R235-1).",
    },
    {
      titre: "Articles R313-3 à R313-7 du Code de la route",
      detail:
        "Obligations d'éclairage d'un véhicule 2RM : feux de croisement allumés en permanence (R313-4), conditions d'utilisation des feux de route (R416-6) et signalisation latérale.",
    },
    {
      titre: "Norme ECE 22.06 (casques) et CE EN 1621-4 (airbags) / EN 13594 (gants)",
      detail:
        "Équipements de protection homologués obligatoires (casque, gants) ou fortement recommandés (airbag, blouson/pantalon CE niveau A, bottes). Référence pédagogique systématique en Passerelle.",
    },
    {
      titre: "REMC — Référentiel pour l'Éducation à une Mobilité Citoyenne",
      detail:
        "Compétences C2 (circuler en conditions normales) et C3 (conditions difficiles, trajectoires de sécurité). Niveau 3 de la matrice GDE (contexte de la conduite).",
    },
    {
      titre: "Données ONISR 2024 / Étude MAIDS",
      detail:
        "25 % des morts sur la route sont des motards (2 % du trafic), 40 % des accidents mortels surviennent en virage. Base statistique pour reconceptualiser le risque A2 → A.",
    },
  ],
};

export default passerelleA2ACours;
